import { ApiPaths } from 'api/constants';
import {
  ReusableFunctionDetails,
  ReusableFunctionGeneral,
} from 'api/endpoints/function';
import {
  CreateProcessParams,
  ProcessDetails,
  ProcessGeneral,
  ProcessStatus,
} from 'api/endpoints/processes';
import {
  FieldBase,
  FieldGeneral,
  ProcessAlias,
} from 'api/endpoints/processFields';
import { GetAllFormsResult } from 'api/endpoints/processForm';
import { ProcessInstanceGeneral } from 'api/endpoints/processInstance';
import { UploadFileWhileExecuteResult } from 'api/endpoints/processScan';
import {
  ProcessVersionDetails,
  ProcessVersionGeneral,
} from 'api/endpoints/processVersions';
import {
  ProcessDefinitionStageDetails,
  ProcessDefinitionStageGeneral,
} from 'api/endpoints/stage';
import { TenantGeneral } from 'api/endpoints/tenants';
import { GetTmsConnectionsResult } from 'api/endpoints/tmsConnections';
import ApiUrlParams from 'constants/apiUrlParams';
import { UPLOAD_FILE_FIELD_NAME } from 'constants/global';
import BaseRepo from 'container/BaseRepo';
import { buildBadRequestApiError } from 'container/helpers/errors';
import { ApiResponse, ApiResponseAsync, isApiError } from 'container/models';
import { SearchRequest } from 'container/search';
import { SendFiles } from 'features/sendFiles/typing';
import {
  CreateFunctionParams,
  CreateStageParams,
  UpdateFunctionParams,
  UpdateStageParams,
} from 'pages/processEditor/types';

import { ActiveReleaseFields, IProcessRepo } from './IProcessRepo';

const { REACT_APP_API_DOCUMENT_SERVICE: documentServiceUrl } = import.meta.env;

export class ProcessRepo extends BaseRepo implements IProcessRepo {
  deleteProcess = (draft: ProcessGeneral): ApiResponseAsync<void> =>
    this.helpers.handleDelete(
      ApiPaths.processes.byId._({
        [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
      })
    );
  createVersion(
    draft: ProcessGeneral
  ): ApiResponseAsync<ProcessVersionGeneral> {
    const link = ApiPaths.processes.byId.versions._({
      [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
    });

    return this.helpers.handlePost(link, {});
  }
  activateVersion(version: ProcessVersionGeneral): ApiResponseAsync<void> {
    const link = ApiPaths.processes.byId.versions.byId.activate._({
      [ApiUrlParams.releaseOrDraftProcessId]: version.draftProcessId,
      [ApiUrlParams.processVersionId]: version.id,
    });

    return this.helpers.handlePostAction(link, {});
  }
  sendFiles = (
    draft: ProcessGeneral,
    data: SendFiles
  ): ApiResponseAsync<void> => {
    const link = ApiPaths.processes.byId.sendEmail._({
      [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
    });
    return this.helpers.handlePost(link, data);
  };
  updateProcess = (
    draft: ProcessGeneral,
    payload: CreateProcessParams
  ): ApiResponseAsync<ProcessGeneral> => {
    const link = ApiPaths.processes.byId._({
      [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
    });

    return this.helpers.handlePut(link, payload);
  };

  createProcess = (
    payload: CreateProcessParams
  ): ApiResponseAsync<ProcessGeneral> => {
    const link = ApiPaths.processes._();

    return this.helpers.handlePost(link, payload);
  };

  getFunctionDetails = (
    releaseOrDraftProcessId: string,
    functionId: string
  ): ApiResponseAsync<ReusableFunctionDetails> => {
    const url = ApiPaths.processes.byId.functions.byId._({
      [ApiUrlParams.releaseOrDraftProcessId]: releaseOrDraftProcessId,
      [ApiUrlParams.functionId]: functionId,
    });
    return this.helpers.handleGet<ReusableFunctionDetails>(url);
  };

  createFunction = (
    draft: ProcessGeneral,
    name: string
  ): ApiResponseAsync<ReusableFunctionDetails> => {
    const url = ApiPaths.processes.byId.functions._({
      [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
    });
    const payload: CreateFunctionParams = {
      name,
    };

    return this.helpers.handlePost(url, payload);
  };

  updateFunction = (
    draft: ProcessGeneral,
    functionId: string,
    name: string,
    schema: string
  ): ApiResponseAsync<ReusableFunctionDetails> => {
    const url = ApiPaths.processes.byId.functions.byId._({
      [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
      [ApiUrlParams.functionId]: functionId,
    });
    const payload: UpdateFunctionParams = {
      name,
      schema,
    };

    return this.helpers.handlePut(url, payload);
  };

  deleteFunction = (
    draft: ProcessGeneral,
    functionId: string
  ): ApiResponseAsync<void> => {
    const url = ApiPaths.processes.byId.functions.byId._({
      [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
      [ApiUrlParams.functionId]: functionId,
    });

    return this.helpers.handleDelete(url);
  };

  getAllFunctions = (
    releaseOrDraftProcessId: string
  ): ApiResponseAsync<ReusableFunctionGeneral[]> => {
    const url = ApiPaths.processes.byId.functions._({
      [ApiUrlParams.releaseOrDraftProcessId]: releaseOrDraftProcessId,
    });
    return this.helpers.handleGet<ReusableFunctionGeneral[]>(url);
  };

  deleteStage = (
    draft: ProcessGeneral,
    stageId: string
  ): ApiResponseAsync<void> => {
    const url = ApiPaths.processes.byId.stages.byId._({
      [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
      [ApiUrlParams.stageId]: stageId,
    });

    return this.helpers.handleDelete(url);
  };

  updateStage = (
    draft: ProcessGeneral,
    stageId: string,
    name: string,
    index: number,
    schema: string
  ): ApiResponseAsync<ProcessDefinitionStageDetails> => {
    const url = ApiPaths.processes.byId.stages.byId._({
      [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
      [ApiUrlParams.stageId]: stageId,
    });
    const payload: UpdateStageParams = {
      name,
      index,
      schema,
    };

    return this.helpers.handlePut(url, payload);
  };

  createStage = (
    draft: ProcessGeneral,
    name: string,
    index: number
  ): ApiResponseAsync<ProcessDefinitionStageDetails> => {
    const url = ApiPaths.processes.byId.stages._({
      [ApiUrlParams.releaseOrDraftProcessId]: draft.id,
    });
    const payload: CreateStageParams = {
      name,
      index,
    };

    return this.helpers.handlePost(url, payload);
  };

  getStageDetails = (
    releaseOrDraftId: string,
    stageId: string
  ): ApiResponseAsync<ProcessDefinitionStageDetails> => {
    const url = ApiPaths.processes.byId.stages.byId._({
      [ApiUrlParams.releaseOrDraftProcessId]: releaseOrDraftId,
      [ApiUrlParams.stageId]: stageId,
    });
    return this.helpers.handleGet<ProcessDefinitionStageDetails>(url);
  };

  getAllStages = (
    releaseOrDraftProcessId: string
  ): ApiResponseAsync<ProcessDefinitionStageGeneral[]> => {
    const url = ApiPaths.processes.byId.stages._({
      [ApiUrlParams.releaseOrDraftProcessId]: releaseOrDraftProcessId,
    });
    return this.helpers.handleGet<ProcessDefinitionStageGeneral[]>(url);
  };

  getProcessDraft = async (
    draftProcessId: string
  ): Promise<ApiResponse<ProcessDetails>> => {
    const url = ApiPaths.processes.byId._({
      [ApiUrlParams.releaseOrDraftProcessId]: draftProcessId,
    });
    const response = await this.helpers.handleGet<ProcessDetails>(url);
    if (isApiError(response)) {
      return response;
    }

    if (response.state !== 'draft') {
      return buildBadRequestApiError(new Error('process is not draft'));
    }

    return response;
  };

  getActiveReleaseFields = async (
    draft: ProcessGeneral
  ): ApiResponseAsync<ActiveReleaseFields> => {
    const link = draft._links?.['getActiveReleaseFields'];
    if (!link) {
      return buildBadRequestApiError(
        new Error('link "get active release fields" is not available')
      );
    }

    return this.helpers.handleGet<ActiveReleaseFields>(link.href);
  };

  getReleaseFields = async (
    processInstance: ProcessInstanceGeneral,
    query: SearchRequest
  ): ApiResponseAsync<DataWithTotal<FieldGeneral>> => {
    const link = processInstance._links?.['getDataModel'];
    if (!link) {
      return buildBadRequestApiError(
        new Error('link "get data model" is not available')
      );
    }

    return this.helpers.executeSearch<DataWithTotal<FieldGeneral>>(link, query);
  };

  uploadFile = async (
    file: File,
    release: ProcessGeneral,
    field: FieldBase
  ): ApiResponseAsync<UploadFileWhileExecuteResult> => {
    const fileFormData = new FormData();
    fileFormData.append(UPLOAD_FILE_FIELD_NAME, file);

    const relativeUrl = ApiPaths.field.byScanInstanceId.byFieldId._({
      [ApiUrlParams.fieldId]: field.id,
      [ApiUrlParams.processInstanceId]: release.id,
    });
    return this.helpers.handlePost(relativeUrl, fileFormData, {
      baseUrl: documentServiceUrl,
    });
  };

  importProcess = (
    file: File,
    tenant: TenantGeneral
  ): ApiResponseAsync<ProcessGeneral> => {
    const fileFormData = new FormData();
    fileFormData.append('process', file);

    const relativeUrl = ApiPaths.processes.import.byTenantId._({
      [ApiUrlParams.tenantId]: tenant.id,
    });

    return this.helpers.handlePost(relativeUrl, fileFormData);
  };

  deleteFile = async (
    payloadFromUploadFile: UploadFileWhileExecuteResult
  ): ApiResponseAsync<void> => {
    const link = payloadFromUploadFile._links?.['delete'];
    if (!link) {
      return buildBadRequestApiError(
        new Error('link "delete file" is not available')
      );
    }

    return this.helpers.handleDelete('', {
      baseUrl: link.href,
    });
  };

  uploadFileWithAlias = async (
    file: File,
    alias: ProcessAlias
  ): ApiResponseAsync<UploadFileWhileExecuteResult> => {
    const link = alias._links?.['uploadFile'];
    if (!link) {
      return buildBadRequestApiError(
        new Error('link "upload file with alias" is not available')
      );
    }

    const fileFormData = new FormData();
    fileFormData.append(UPLOAD_FILE_FIELD_NAME, file);

    return this.helpers.handlePost(link.href, fileFormData);
  };

  getFieldGeneralFields = async (
    releaseOrDraftProcessId: string
  ): ApiResponseAsync<DataWithTotal<FieldGeneral>> => {
    const url = ApiPaths.processes.byId.fields._({
      [ApiUrlParams.releaseOrDraftProcessId]: releaseOrDraftProcessId,
    });
    return this.helpers.handleGet<DataWithTotal<FieldGeneral>>(url);
  };

  getAllForms = async (
    draftProcessId: string
  ): ApiResponseAsync<GetAllFormsResult> => {
    const url = ApiPaths.processes.byId.processForms._({
      [ApiUrlParams.releaseOrDraftProcessId]: draftProcessId,
    });
    return this.helpers.handleGet<GetAllFormsResult>(url);
  };

  getTmsConnections = async (): ApiResponseAsync<GetTmsConnectionsResult> => {
    const url = ApiPaths.tmsConnections._();
    return this.helpers.handleGet<GetTmsConnectionsResult>(url);
  };

  getVersionDetails = async (
    draftProcessId: string,
    processVersionId: string
  ): ApiResponseAsync<ProcessVersionDetails> => {
    const url = ApiPaths.processes.byId.versions.byId._({
      [ApiUrlParams.releaseOrDraftProcessId]: draftProcessId,
      [ApiUrlParams.processVersionId]: processVersionId,
    });

    return this.helpers.handleGet<ProcessVersionDetails>(url);
  };

  getProcesses = async (
    state: 'draft' | 'release',
    status?: ProcessStatus,
    paginationParams?: Pagination
  ): ApiResponseAsync<DataWithTotal<ProcessGeneral>> => {
    const params = new URLSearchParams({
      state,
      offset: paginationParams?.offset?.toString() ?? '0',
      limit: paginationParams?.limit?.toString() ?? '1000',
    });

    if (status) params.set('processStatus', status);

    const finalUrl = ApiPaths.processes._() + '?' + params.toString();

    return this.helpers.handleGet<DataWithTotal<ProcessGeneral>>(finalUrl);
  };
}
